import { Form } from "react-bootstrap";

import EnumerationField from "./EnumerationField";
import ParameterMapEditor from "./ParameterMapEditor";
import ParameterTypeSelect from "./ParameterTypeSelect";
import { FnParameter } from "../../types";

const ParameterDetails = ({
  path,
  depth,
  parameter,
  onChange,
}: {
  path: string;
  depth: number;
  parameter: FnParameter;
  onChange: (next: FnParameter) => void;
}) => {
  const updateBound = (
    name: "min_items" | "max_items",
    value: string
  ): void => {
    if (parameter.value_type !== "array") {
      return;
    }
    const next = { ...parameter };
    const count = Number(value);
    if (value === "" || Number.isNaN(count)) {
      delete next[name];
    } else {
      next[name] = count;
    }
    onChange(next);
  };

  return (
    <>
      <Form.Control
        className="mt-2"
        placeholder="Description"
        value={parameter.docs ?? ""}
        onChange={(event) =>
          onChange({ ...parameter, docs: event.target.value || null })
        }
        data-testid={`${path}-docs`}
      />
      {(parameter.value_type === "string" ||
        parameter.value_type === "integer" ||
        parameter.value_type === "number") && (
        <div className="mt-2">
          <Form.Label
            htmlFor={`${path}-enum`}
            className="small text-body-secondary mb-1"
          >
            Valid values
          </Form.Label>
          <EnumerationField
            id={`${path}-enum`}
            valueType={parameter.value_type}
            enumeration={parameter.enumeration}
            onChange={(enumeration) => {
              const next = { ...parameter };
              if (enumeration === null) {
                delete next.enumeration;
              } else {
                next.enumeration = enumeration;
              }
              onChange(next);
            }}
          />
        </div>
      )}
      {parameter.value_type === "array" && (
        <div className="mt-2 border-start ps-3">
          <div className="d-flex align-items-center gap-2">
            <Form.Label
              htmlFor={`${path}-items-type`}
              className="small text-body-secondary mb-0"
            >
              Items
            </Form.Label>
            <ParameterTypeSelect
              id={`${path}-items-type`}
              parameter={parameter.items}
              onChange={(items) => onChange({ ...parameter, items })}
            />
            <Form.Control
              type="number"
              min={0}
              style={{ width: "90px" }}
              placeholder="Min"
              aria-label="Minimum items"
              value={parameter.min_items ?? ""}
              onChange={(event) => updateBound("min_items", event.target.value)}
              data-testid={`${path}-min-items`}
            />
            <Form.Control
              type="number"
              min={0}
              style={{ width: "90px" }}
              placeholder="Max"
              aria-label="Maximum items"
              value={parameter.max_items ?? ""}
              onChange={(event) => updateBound("max_items", event.target.value)}
              data-testid={`${path}-max-items`}
            />
          </div>
          <ParameterDetails
            path={`${path}-items`}
            depth={depth}
            parameter={parameter.items}
            onChange={(items) => onChange({ ...parameter, items })}
          />
        </div>
      )}
      {parameter.value_type === "object" && (
        <div className="mt-2 border-start ps-3">
          <div className="small text-body-secondary mb-1">Properties</div>
          <ParameterMapEditor
            path={`${path}-prop`}
            noun="property"
            depth={depth + 1}
            map={{
              parameters: parameter.properties,
              required: parameter.required,
            }}
            onChange={({ parameters, required }) =>
              onChange({ ...parameter, properties: parameters, required })
            }
          />
        </div>
      )}
    </>
  );
};

export default ParameterDetails;
